import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Lock } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Countdown } from "./Countdown";
import { MaskedLines } from "./Reveal";

const inputCls = "w-full bg-transparent border border-[#27272A] px-4 py-3 text-sm outline-none focus:border-[#FFCC00] placeholder:text-white/30";

export const FlashLock = () => {
  const { user, setUser } = useAuth();
  const location = useLocation();
  const [show, setShow] = useState(false);
  const [hero, setHero] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const suppressed = ["/login", "/join", "/admin", "/checkout", "/payment-return"].some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (user === null) return;
    if (user) return;
    if (suppressed) return;
    if (sessionStorage.getItem("ukj_flashlock")) return;
    api.get("/deals/hero").then(({ data }) => { setHero(data); setShow(true); }).catch(() => {});
  }, [user, suppressed]);

  useEffect(() => {
    if (suppressed && show) setShow(false);
  }, [suppressed, show]);

  const dismiss = () => {
    sessionStorage.setItem("ukj_flashlock", "1");
    setShow(false);
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const register = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/register", form);
      setUser(data);
      toast.success("Welcome to the Club. Prices unlocked.");
      dismiss();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {show && hero && (
        <motion.div
          data-testid="flash-lock-overlay"
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-xl flex items-center justify-center p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
        >
          <motion.div
            className="max-w-2xl w-full border border-[#27272A] bg-[#0A0A0A] relative my-8"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="hazard-stripes h-2" />
            <div className="p-8 sm:p-10">
              <div className="bg-white px-4 py-2 inline-flex items-center mb-5">
                <img src="/logo.png" alt="UKJobLots" className="h-9 w-auto" />
              </div>
              <div className="flex items-center gap-2 text-[#FF3B30] text-xs uppercase tracking-[0.3em] mb-5">
                <Lock size={12} /> Members only pricing
              </div>
              <MaskedLines
                lines={["TODAY'S DEAL", "IS LOCKED."]}
                className="font-display text-4xl sm:text-6xl leading-[0.95]"
                delay={0.3}
              />
              <div className="mt-6 border border-[#27272A] p-4 flex gap-4 items-center">
                <img src={hero.images[0]} alt={hero.title} className="w-20 h-20 object-cover grayscale" />
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">{hero.sku} · {hero.condition}</div>
                  <div className="font-display text-lg leading-tight truncate">{hero.title}</div>
                  <div className="flex items-center gap-4 mt-1">
                    <span className="font-display text-xl text-[#FFCC00] price-locked">£888.00</span>
                    <Countdown endsAt={hero.ends_at} />
                  </div>
                </div>
              </div>
              <form onSubmit={register} className="mt-6 space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <input data-testid="flash-lock-name-input" required placeholder="FULL NAME" value={form.name} onChange={set("name")} className={inputCls} />
                  <input data-testid="flash-lock-email-input" required type="email" placeholder="EMAIL ADDRESS" value={form.email} onChange={set("email")} className={inputCls} />
                </div>
                <input data-testid="flash-lock-password-input" required type="password" placeholder="PASSWORD (MIN 6 CHARS)" value={form.password} onChange={set("password")} className={inputCls} />
                {error && <div data-testid="flash-lock-error" className="text-[#FF3B30] text-xs border border-[#FF3B30] p-3">{error}</div>}
                <button data-testid="flash-lock-unlock-button" disabled={busy} className="btn-industrial w-full py-4 text-xl">
                  {busy ? "UNLOCKING…" : "UNLOCK MEMBER PRICES — FREE"}
                </button>
              </form>
              <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs">
                <span className="text-white/40">
                  Already a member?{" "}
                  <Link to="/login" onClick={dismiss} data-testid="flash-lock-login-link" className="text-[#FFCC00] hover:text-white">Log in</Link>
                </span>
                <button data-testid="flash-lock-dismiss-button" onClick={dismiss} className="text-white/40 uppercase tracking-widest border-b border-white/20 hover:text-white hover:border-white">
                  Continue browsing without prices
                </button>
              </div>
              <p className="text-white/30 text-[11px] mt-4 text-center">Free forever. No card required. {hero.stock_remaining} lots left on today's deal.</p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
